import React from "react";
import { Mechanism } from "../../services/mechanismsClient";
import { parseReferences } from "../../utils/utils";
import { HUBBLE_LITERATURE_URL } from "../../config";

interface LiteratureTabProps {
  mechanism: Mechanism;
  gene: string;
}

export const LiteratureTab: React.FC<LiteratureTabProps> = ({
  mechanism,
  gene,
}) => {
  // Check if we have target_link and disease_link data
  const hasTargetLink = (mechanism as any).target_link;
  const hasDiseaseLink = (mechanism as any).disease_link;
  const diseaseRaw = (mechanism as any)?.target_disease_link?.disease;

  const formatDiseaseName = (name: string) => {
    return name
      .replace(/_/g, " ")
      .split(" ")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const disease = hasDiseaseLink?.disease
    ? formatDiseaseName(hasDiseaseLink.disease)
    : diseaseRaw
      ? formatDiseaseName(diseaseRaw)
      : "the disease";

  if (!hasTargetLink && !hasDiseaseLink) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <div className="text-6xl mb-4">📚</div>
        <p>No literature evidence available for this mechanism</p>
      </div>
    );
  }

  const targetLink = (mechanism as any).target_link;
  const diseaseLink = (mechanism as any).disease_link;

  return (
    <div className="space-y-8">
      {/* Disease Link Section - Apoptosis -> Cardiovascular */}
      {diseaseLink && (
        <div className="space-y-4">
          <div className="bg-indigo-600 dark:bg-indigo-700 text-white px-4 py-3 rounded-t-lg">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              📚 Evidence for link of '{mechanism.name}' to {disease}
            </h2>
          </div>

          {diseaseLink.evidence_summary && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-indigo-500 dark:border-indigo-600 overflow-hidden p-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {diseaseLink.evidence_summary}
              </p>
            </div>
          )}

          {!diseaseLink.strong_evidence &&
            !diseaseLink.weak_evidence &&
            !diseaseLink.negative_evidence ? (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No evidence available
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Strong Evidence */}
              {diseaseLink.strong_evidence && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-green-500 dark:border-green-600 overflow-hidden">
                  <div className="bg-green-50 dark:bg-green-950 px-4 py-2">
                    <span className="inline-block bg-green-500 dark:bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Strong Evidence
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseReferences(
                        diseaseLink.strong_evidence,
                        (mechanism as any).references || {},
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Weak Evidence */}
              {diseaseLink.weak_evidence && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-orange-500 dark:border-orange-600 overflow-hidden">
                  <div className="bg-orange-50 dark:bg-orange-950 px-4 py-2">
                    <span className="inline-block bg-orange-500 dark:bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Weak Evidence
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseReferences(
                        diseaseLink.weak_evidence,
                        (mechanism as any).references || {},
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Target Link Section - STAT4 -> Apoptosis */}
      {targetLink && (
        <div className="space-y-4">
          <div className="bg-indigo-600 dark:bg-indigo-700 text-white px-4 py-3 rounded-t-lg">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              📚 Evidence for involvement of {gene} in '{mechanism.name}'
            </h2>
          </div>

          {targetLink.evidence_summary && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-indigo-500 dark:border-indigo-600 overflow-hidden p-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {targetLink.evidence_summary}
              </p>
            </div>
          )}

          {!targetLink.strong_evidence &&
            !targetLink.weak_evidence &&
            !targetLink.negative_evidence ? (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                No evidence available
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Strong Evidence */}
              {targetLink.strong_evidence && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-green-500 dark:border-green-600 overflow-hidden">
                  <div className="bg-green-50 dark:bg-green-950 px-4 py-2">
                    <span className="inline-block bg-green-500 dark:bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Strong Evidence
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseReferences(
                        targetLink.strong_evidence,
                        (mechanism as any).references || {},
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Weak Evidence */}
              {targetLink.weak_evidence && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-orange-500 dark:border-orange-600 overflow-hidden">
                  <div className="bg-orange-50 dark:bg-orange-950 px-4 py-2">
                    <span className="inline-block bg-orange-500 dark:bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Weak Evidence
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseReferences(
                        targetLink.weak_evidence,
                        (mechanism as any).references || {},
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Negative Evidence */}
              {targetLink.negative_evidence && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-red-500 dark:border-red-600 overflow-hidden">
                  <div className="bg-red-50 dark:bg-red-950 px-4 py-2">
                    <span className="inline-block bg-red-500 dark:bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Negative Evidence
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {parseReferences(
                        targetLink.negative_evidence,
                        (mechanism as any).references || {},
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* Rating reason */}
      {mechanism.rating_reason && (
        <div className="space-y-4">
          <div className="bg-indigo-600 dark:bg-indigo-700 text-white px-4 py-3 rounded-t-lg">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              📚 Rating rationale
              <span
                className="text-gray-200 dark:text-gray-300 text-sm cursor-help"
                title="An explanation for how the given rating was derived. General advice: treat this rating with caution."
              >
                ⓘ
              </span>
            </h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-violet-500 dark:border-violet-600 overflow-hidden">
            <div className="p-4">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {mechanism.rating_reason}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* References Section */}
      <div className="space-y-4 mt-8">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          📖 References
        </h3>
        {(mechanism as any).references &&
          Object.keys((mechanism as any).references).length > 0 ? (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <ul className="space-y-4">
              {Object.entries((mechanism as any).references).map(
                ([key, ref]: [string, any], index) => (
                  <li
                    key={ref.pid || `ref-${index}`}
                    className="p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center min-w-[2.5rem] h-6 bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200 text-xs font-bold rounded px-2">
                        {key}
                      </span>
                      <div className="flex-1 space-y-2">
                        {ref.title && (
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-relaxed">
                            {ref.title}
                          </p>
                        )}
                        {ref.pid && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            PubMed ID:{" "}
                            <a
                              href={`${HUBBLE_LITERATURE_URL}${ref.pid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={ref.title}
                              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium underline decoration-blue-400 dark:decoration-blue-500 hover:decoration-blue-600 dark:hover:decoration-blue-400 decoration-1 underline-offset-2 transition-colors duration-150"
                            >
                              {ref.pid}
                              <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                ),
              )}
            </ul>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              No references available for this mechanism
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
